import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { RailNavItem } from "./NavData";
import { RAIL_GROUPS } from "./NavData";
import { useRailCoachmark } from "./useRailPreferences";

/** Width of the rail when expanded (pinned or peeking). Collapsed = --rail-w. */
export const RAIL_W_EXPANDED = "228px";

interface RailProps {
  items: RailNavItem[];
  activeId: string;
  onPick: (id: string) => void;
  userInitials?: string;
  onUserClick?: () => void;
  /** Persisted: keep the rail expanded with labels (UX review option C). */
  pinned: boolean;
  /** Toggle the pinned preference. */
  onTogglePin: () => void;
}

const Chevron: React.FC<{ dir: "left" | "right" }> = ({ dir }) => (
  <svg
    width={16}
    height={16}
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    style={{ flexShrink: 0 }}
  >
    <path
      d={dir === "left" ? "M10 4 L6 8 L10 12" : "M6 4 L10 8 L6 12"}
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ─── One-time coachmark (UX review option D) ───────────────────────────────────

const RailCoachmark: React.FC<{ onPin: () => void; onDismiss: () => void }> = ({
  onPin,
  onDismiss,
}) => {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onDismiss();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onDismiss]);

  return (
    <div
      role="dialog"
      aria-label="Navigation tip"
      style={{
        position: "fixed",
        left: "calc(var(--rail-w) + 12px)",
        top: 84,
        zIndex: 70,
        width: 250,
        background: "var(--bg-elev)",
        border: "1px solid var(--line)",
        borderRadius: "var(--r-md)",
        boxShadow: "var(--sh-pop)",
        padding: 14,
      }}
    >
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: "var(--ink-1)",
          marginBottom: 6,
        }}
      >
        Looking for menu names?
      </div>
      <div
        style={{
          fontSize: 12.5,
          color: "var(--ink-2)",
          lineHeight: 1.5,
          marginBottom: 12,
        }}
      >
        Icons keep the menu compact. Click the arrow at the bottom to pin the
        menu open and keep labels visible.
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button type="button" className="btn sm ghost" onClick={onDismiss}>
          Got it
        </button>
        <button type="button" className="btn sm accent" onClick={onPin}>
          Pin it open
        </button>
      </div>
    </div>
  );
};

// ─── Rail ───────────────────────────────────────────────────────────────────

const Rail: React.FC<RailProps> = ({
  items,
  activeId,
  onPick,
  userInitials,
  onUserClick,
  pinned,
  onTogglePin,
}) => {
  const [tip, setTip] = useState<{ label: string; top: number } | null>(null);
  // Hovered item id, tracked in state (not imperative el.style) so the hover
  // highlight is part of the render and can't be clobbered by a re-render
  // mid-hover — that clobbering was the "flashing".
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const { seen: coachSeen, markSeen } = useRailCoachmark();

  // The rail is either pinned open (with labels) or collapsed (icons only).
  // Opening the account menu must NOT expand it — the menu opens next to the
  // collapsed rail. Hovering shows a tooltip but never expands.
  const expanded = pinned;

  // Tooltip (UX review option A) — fires on both mouse hover and keyboard focus
  // when the rail is collapsed (not pinned). Portalled so rail overflow:hidden
  // can't clip it.
  const showTip = useCallback(
    (e: React.FocusEvent | React.MouseEvent, label: string) => {
      const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
      setTip({ label, top: r.top + r.height / 2 });
    },
    [],
  );
  const hideTip = useCallback(() => setTip(null), []);

  // Escape dismisses the tooltip.
  useEffect(() => {
    if (!tip) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setTip(null);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [tip]);

  const groups = RAIL_GROUPS.map((name) => ({
    name,
    items: items.filter((item) => item.group === name),
  })).filter((g) => g.items.length > 0);

  const navButtonStyle = (
    isActive: boolean,
    isHovered: boolean,
  ): React.CSSProperties => ({
    position: "relative",
    boxSizing: "border-box",
    width: expanded ? "100%" : 40,
    height: 40,
    borderRadius: 8,
    border: "none",
    background: isActive
      ? "color-mix(in oklab, var(--accent) 18%, transparent)"
      : isHovered
        ? "color-mix(in oklab, var(--bg-rail-fg) 12%, transparent)"
        : "transparent",
    color:
      isActive || isHovered ? "var(--bg-rail-fg-active)" : "var(--bg-rail-fg)",
    display: "flex",
    alignItems: "center",
    justifyContent: expanded ? "flex-start" : "center",
    gap: 12,
    padding: expanded ? "0 12px" : 0,
    cursor: "pointer",
    // Enterprise nav: a short, decelerating fade reads as crisp/responsive
    // (the highlight tracks the cursor) rather than a slow fade that lags.
    transition:
      "background 150ms cubic-bezier(0.4, 0, 0.2, 1), color 150ms cubic-bezier(0.4, 0, 0.2, 1)",
    flexShrink: 0,
    margin: "2px 0",
    font: "inherit",
    textAlign: "left",
  });

  const labelStyle = (isActive: boolean): React.CSSProperties => ({
    fontSize: 13,
    fontWeight: isActive ? 600 : 500,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    flex: 1,
  });

  const renderNavButton = (item: RailNavItem) => {
    const isActive = activeId === item.id;
    const isHovered = hoveredId === item.id;
    const Icon = item.icon;
    return (
      <button
        key={item.id}
        type="button"
        onClick={() => onPick(item.id)}
        aria-label={item.label}
        aria-current={isActive ? "page" : undefined}
        onMouseEnter={(e) => {
          setHoveredId(item.id);
          showTip(e, item.label);
        }}
        onMouseLeave={() => {
          setHoveredId(null);
          hideTip();
        }}
        onFocus={(e) => showTip(e, item.label)}
        onBlur={hideTip}
        style={navButtonStyle(isActive, isHovered)}
      >
        {isActive && (
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              left: expanded ? 0 : -12,
              top: 12,
              width: 3,
              height: 16,
              borderRadius: "0 2px 2px 0",
              background: "var(--accent)",
            }}
          />
        )}
        <Icon size={18} />
        {expanded && <span style={labelStyle(isActive)}>{item.label}</span>}
      </button>
    );
  };

  return (
    <aside
      aria-label="Main navigation rail"
      style={{
        gridArea: "rail",
        background: "var(--bg-rail)",
        display: "flex",
        flexDirection: "column",
        alignItems: expanded ? "stretch" : "center",
        padding: "12px 0 16px",
        gap: 0,
        // The rail always fills its grid column — AppShell owns the width
        // (64px collapsed / RAIL_W_EXPANDED pinned), so there's a single source
        // of truth. minWidth:0 is load-bearing: a grid item defaults to
        // min-width:auto and would otherwise refuse to shrink below its content
        // (the labels), overflowing the 64px track and covering the page.
        width: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        height: "100%",
        overflow: "hidden",
        zIndex: 30,
      }}
    >
      {/* Logo / wordmark */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          width: expanded ? "100%" : "auto",
          padding: expanded ? "0 14px" : 0,
          marginBottom: 8,
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 8,
            background: "var(--accent)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--accent-ink)",
            fontFamily: "var(--font-display, var(--font-sans))",
            fontWeight: 700,
            fontSize: 17,
            flexShrink: 0,
          }}
        >
          α
        </div>
        {expanded && (
          <span
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: "var(--bg-rail-fg-active)",
              whiteSpace: "nowrap",
            }}
          >
            AlphaHealth
          </span>
        )}
      </div>

      {/* Divider */}
      <div
        style={{
          width: expanded ? "calc(100% - 24px)" : 24,
          height: 1,
          background: "color-mix(in oklab, var(--bg-rail-fg) 30%, transparent)",
          margin: "4px 0 8px",
          flexShrink: 0,
        }}
      />

      {/* Nav groups */}
      <nav
        aria-label="Section navigation"
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 0,
          width: "100%",
          alignItems: expanded ? "stretch" : "center",
          padding: expanded ? "0 12px" : 0,
          boxSizing: "border-box",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {groups.map((group, gi) => (
          <React.Fragment key={group.name}>
            {expanded ? (
              // Section header in the expanded rail (IA scannability).
              <div
                style={{
                  width: "100%",
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color:
                    "color-mix(in oklab, var(--bg-rail-fg) 55%, transparent)",
                  padding: gi === 0 ? "2px 12px 4px" : "12px 12px 4px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  flexShrink: 0,
                }}
              >
                {group.name}
              </div>
            ) : (
              gi > 0 && (
                <div
                  style={{
                    width: 24,
                    height: 1,
                    background:
                      "color-mix(in oklab, var(--bg-rail-fg) 20%, transparent)",
                    margin: "6px 0",
                    flexShrink: 0,
                  }}
                />
              )
            )}
            {group.items.map(renderNavButton)}
          </React.Fragment>
        ))}
      </nav>

      {/* Pin / collapse toggle (UX review option C) */}
      <div
        style={{
          width: "100%",
          padding: expanded ? "0 12px" : 0,
          boxSizing: "border-box",
          display: "flex",
          justifyContent: expanded ? "flex-start" : "center",
          flexShrink: 0,
          marginTop: 4,
        }}
      >
        <button
          type="button"
          onClick={onTogglePin}
          aria-pressed={pinned}
          aria-label={pinned ? "Collapse navigation" : "Pin navigation open"}
          onMouseEnter={(e) => {
            setHoveredId("__pin");
            showTip(e, pinned ? "Collapse menu" : "Pin menu open");
          }}
          onMouseLeave={() => {
            setHoveredId(null);
            hideTip();
          }}
          onFocus={(e) =>
            showTip(e, pinned ? "Collapse menu" : "Pin menu open")
          }
          onBlur={hideTip}
          style={navButtonStyle(false, hoveredId === "__pin")}
        >
          <Chevron dir={pinned ? "left" : "right"} />
          {expanded && (
            <span style={labelStyle(false)}>
              {pinned ? "Collapse menu" : "Pin menu open"}
            </span>
          )}
        </button>
      </div>

      {/* User avatar */}
      <div
        style={{
          width: "100%",
          padding: expanded ? "0 12px" : 0,
          boxSizing: "border-box",
          display: "flex",
          justifyContent: expanded ? "flex-start" : "center",
          flexShrink: 0,
          marginTop: 8,
        }}
      >
        <button
          type="button"
          onClick={onUserClick}
          aria-label="Account menu"
          onMouseEnter={() => setHoveredId("__account")}
          onMouseLeave={() => setHoveredId(null)}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: expanded ? "flex-start" : "center",
            gap: 10,
            boxSizing: "border-box",
            width: expanded ? "100%" : 40,
            height: 40,
            padding: expanded ? "0 8px" : 0,
            borderRadius: 8,
            background:
              hoveredId === "__account"
                ? "color-mix(in oklab, var(--bg-rail-fg) 12%, transparent)"
                : "transparent",
            border: "none",
            cursor: "pointer",
            font: "inherit",
            transition: "background 150ms cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <span
            style={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              background: "var(--accent)",
              color: "var(--accent-ink)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 11,
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {userInitials ?? "?"}
          </span>
          {expanded && (
            <span
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: "var(--bg-rail-fg)",
                whiteSpace: "nowrap",
              }}
            >
              Account
            </span>
          )}
        </button>
      </div>

      {/* Tooltip (option A) — portal so the rail's overflow:hidden can't clip it */}
      {tip &&
        !expanded &&
        createPortal(
          <div
            role="tooltip"
            style={{
              position: "fixed",
              left: "calc(var(--rail-w) + 8px)",
              top: tip.top,
              transform: "translateY(-50%)",
              background: "var(--surface-inverse)",
              color: "var(--surface-inverse-ink)",
              padding: "4px 9px",
              borderRadius: "var(--r-sm)",
              fontSize: 12,
              fontWeight: 500,
              whiteSpace: "nowrap",
              boxShadow: "var(--sh-2)",
              zIndex: 80,
              pointerEvents: "none",
            }}
          >
            {tip.label}
          </div>,
          document.body,
        )}

      {/* One-time coachmark (option D) */}
      {!coachSeen &&
        !pinned &&
        createPortal(
          <RailCoachmark
            onDismiss={markSeen}
            onPin={() => {
              onTogglePin();
              markSeen();
            }}
          />,
          document.body,
        )}
    </aside>
  );
};

export default Rail;
